import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Robot, CheckCircle, Spinner, ArrowRight, CalendarCheck, ListChecks, Sparkle,
  FacebookLogo, InstagramLogo, EnvelopeSimple, ChatText, Article } from "@phosphor-icons/react";

const STEPS = [
  { at: 0, label: "Marketing Director building strategy", emp: "AI Marketing Director" },
  { at: 8, label: "Social Manager generating Facebook content", emp: "AI Social Manager" },
  { at: 30, label: "Social Manager generating Instagram content", emp: "AI Social Manager" },
  { at: 52, label: "Email Manager writing campaigns", emp: "AI Email Manager" },
  { at: 68, label: "SMS Manager preparing reminders", emp: "AI SMS Manager" },
  { at: 78, label: "Blog Manager writing articles", emp: "AI Blog Manager" },
  { at: 90, label: "Scheduling everything onto the calendar", emp: "AI Marketing Director" },
];
const CH = [
  { key: "facebook", label: "Facebook Posts", icon: FacebookLogo, cls: "text-sky-400" },
  { key: "instagram", label: "Instagram Posts", icon: InstagramLogo, cls: "text-pink-400" },
  { key: "email", label: "Email Campaigns", icon: EnvelopeSimple, cls: "text-emerald-400" },
  { key: "sms", label: "SMS Campaigns", icon: ChatText, cls: "text-violet-400" },
  { key: "blog", label: "Blog Articles", icon: Article, cls: "text-amber-400" },
];

const fmtDuration = (s) => {
  s = Math.max(1, Math.round(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const parts = [];
  if (h) parts.push(`${h} hour${h > 1 ? "s" : ""}`);
  if (m) parts.push(`${m} minute${m > 1 ? "s" : ""}`);
  if (sec || !parts.length) parts.push(`${sec} second${sec !== 1 ? "s" : ""}`);
  return parts.join(" ");
};

export default function GenerationOverlay({ job, estTotal, monthLabel, elapsed, channelCounts, autopilot, onViewCalendar, onOpenQueue, onGenerateAnother, onDismiss }) {
  const total = job?.total || estTotal || 1;
  const created = job?.created || 0;
  const pct = job?.status === "done" ? 100 : Math.min(97, Math.round((created / total) * 90) + (job?.strategy ? 8 : 3));
  const eta = Math.max(5, Math.round((total - created) * 2.2));
  const curIdx = useMemo(() => {
    let i = 0;
    STEPS.forEach((s, k) => { if (pct >= s.at) i = k; });
    return i;
  }, [pct]);

  if (job?.status === "done") {
    const counts = (job.channel_counts && Object.keys(job.channel_counts).length) ? job.channel_counts : (channelCounts || {});
    const createdTotal = Object.values(counts).reduce((a, b) => a + (b || 0), 0) || job.created || 0;
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#0D0D14]/97 backdrop-blur-xl" data-testid="celebration-screen">
        <div className="w-full max-w-xl text-center demo-fade">
          <div className="text-6xl mb-4" style={{ animation: "pop-in .5s cubic-bezier(0.34,1.56,0.64,1) both" }}>🎉</div>
          <h2 className="font-head font-extrabold text-3xl md:text-4xl tracking-tight">Your Monthly Marketing<br />Campaign Is Ready!</h2>
          <p className="mt-3 text-[#9a9a9a]" data-testid="celebration-stats">
            Generated in <span className="text-white font-semibold">{fmtDuration(elapsed)}</span> · <span className="brand-text font-bold">{createdTotal} AI marketing assets</span> created for {monthLabel}
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="celebration-breakdown">
            {CH.filter((c) => counts[c.key]).map((c, i) => (
              <div key={c.key} className="card-matte rounded-xl p-3.5 flex items-center gap-2.5" style={{ animation: `demo-fade .4s ease-out ${i * 0.1 + 0.2}s both` }}>
                <c.icon size={20} className={c.cls} weight="fill" />
                <div className="text-left">
                  <div className="font-head font-bold text-lg leading-none">{counts[c.key]}</div>
                  <div className="text-[10px] text-[#9a9a9a]">{c.label}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-[#7a7a7a]">
            {autopilot ? "Auto Pilot is on — everything is scheduled and will publish automatically. You can still edit or pause anytime."
              : "Everything is waiting in your Approval Queue — nothing publishes until you approve it."}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button data-testid="celebration-view-calendar" onClick={onViewCalendar} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-11 px-6">
              <CalendarCheck size={16} className="mr-2" weight="fill" /> View Calendar
            </Button>
            <Button data-testid="celebration-open-queue" variant="ghost" onClick={onOpenQueue} className="rounded-full border border-white/15 text-white hover:bg-white/5 h-11 px-6">
              <ListChecks size={16} className="mr-2" /> Open Approval Queue
            </Button>
            <Button data-testid="celebration-generate-another" variant="ghost" onClick={onGenerateAnother} className="rounded-full text-[#9a9a9a] hover:text-white hover:bg-white/5 h-11 px-5">
              <Sparkle size={15} className="mr-2" weight="fill" /> Generate Another Month
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#0D0D14]/97 backdrop-blur-xl" data-testid="generation-overlay">
      <div className="w-full max-w-lg demo-fade">
        <div className="text-center mb-8">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-[#6B3DFF]/15 items-center justify-center mb-4">
            <Robot size={32} className="brand-text animate-pulse" weight="duotone" />
          </div>
          <h2 className="font-head font-extrabold text-2xl md:text-3xl tracking-tight">Building Your Monthly<br />Marketing Strategy…</h2>
          <p className="mt-2 text-sm text-[#9a9a9a]" data-testid="generation-current-employee">
            <span className="brand-text font-semibold">{STEPS[curIdx].emp}</span> is working · ~{eta}s remaining
          </p>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-2 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#6B3DFF] to-[#A56CFF] transition-all duration-1000 rounded-full" style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-sm font-bold brand-text w-11 text-right" data-testid="generation-pct">{pct}%</span>
        </div>

        <div className="space-y-2.5" data-testid="generation-steps">
          {STEPS.map((s, k) => {
            const done = k < curIdx;
            const cur = k === curIdx;
            return (
              <div key={s.label} className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 transition-all duration-500 ${cur ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/[0.06]" : done ? "border-white/8 bg-white/[0.02]" : "border-white/5 opacity-40"}`}>
                {done ? <CheckCircle size={17} className="text-emerald-400 shrink-0" weight="fill" />
                  : cur ? <Spinner size={17} className="brand-text shrink-0 animate-spin" />
                    : <div className="h-[17px] w-[17px] rounded-full border border-white/15 shrink-0" />}
                <span className={`text-sm ${cur ? "text-white font-semibold" : done ? "text-[#cfcfcf]" : "text-[#7a7a7a]"}`}>{s.label}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-5 text-center text-xs text-[#7a7a7a]">
          {created}/{total} assets created — you can safely leave this page, generation continues in the background.
        </div>
        <div className="mt-2 text-center">
          <button data-testid="generation-dismiss" onClick={onDismiss} className="text-xs text-[#7a7a7a] hover:text-white underline underline-offset-2">Continue in background <ArrowRight size={11} className="inline" /></button>
        </div>
      </div>
    </div>
  );
}
